import { useState } from 'react';
import { STATS_INTERVAL, type StatsInterval } from '@/lib/leads';

/**
 * The Daily / Weekly / Monthly a person picked on an analytics chart. It
 * stays until they pick another: changing the range doesn't reset it, and it
 * carries across the analytics screens (kept in this browser). Before any
 * pick, each range's own default applies.
 */

const STORAGE_KEY = 'bce.analytics.interval';
const INTERVALS: ReadonlySet<string> = new Set(Object.values(STATS_INTERVAL));

function stored(): StatsInterval | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value !== null && INTERVALS.has(value) ? (value as StatsInterval) : null;
  } catch {
    // Storage blocked (private window, policy): the choice lasts for this screen only.
    return null;
  }
}

/**
 * @param rangeDefault - The range's own interval, used until a pick.
 * @returns The interval to show, and the setter for a pick.
 */
export function useIntervalChoice(rangeDefault: StatsInterval): [StatsInterval, (next: StatsInterval) => void] {
  const [choice, setChoice] = useState<StatsInterval | null>(stored);
  function choose(next: StatsInterval): void {
    setChoice(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage blocked: keep it in memory */
    }
  }
  return [choice ?? rangeDefault, choose];
}
